import React, { useState } from 'react';
import { Formik, FormikProps, Form } from 'formik';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { Grid, TextField, Button, makeStyles, Checkbox, FormControlLabel } from '@material-ui/core';
import * as Yup from 'yup';
import type { TicketInput } from './types';
import { Partner } from '../HotelForm/types';
import AutoSuggest from '../../../Bookings/BookingForm/AutoSuggest';

interface Props {
  initialValues?: TicketInput;
  onSubmit: (values: TicketInput) => void;
  onSuccess?: () => void;
  submitLabel?: string;
  partnerOptions: Array<{ label: string; value: Partner }>;
}

const ticketSchema = Yup.object().shape({
  company: Yup.string().required('Required'),
  numberOfPassengers: Yup.number().positive('Must be a positive number').required('Required'),
  departure: Yup.object()
    .shape({
      date: Yup.string().required('Required')
    })
    .required('Required')
});

const defaultValues: Omit<TicketInput, 'customerId'> = {
  company: '',
  partner: null,
  numberOfPassengers: 0,
  departure: {
    date: new Date(),
    reference: ''
  }
};

const useStyles = makeStyles((theme) => ({
  input: {
    width: '100%'
  },
  button: {
    marginTop: theme.spacing(2)
  }
}));

const TicketForm = ({
  initialValues = defaultValues,
  onSubmit,
  onSuccess,
  partnerOptions,
  submitLabel = 'Add ticket'
}: Props) => {
  const { input, button } = useStyles();
  const [isReturnEnabled, setIsReturnEnabled] = useState<boolean>(true);

  console.log(initialValues);

  const handleSubmit = (values: TicketInput) => {
    const { id, ...rest } = values;
    onSubmit(rest);
    onSuccess?.();
  };

  return (
    <Formik enableReinitialize={true} initialValues={initialValues} onSubmit={handleSubmit} validationSchema={ticketSchema}>
      {({ errors, touched, handleChange, handleBlur, values, setFieldValue }: FormikProps<TicketInput>) => (
        <Form>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  id="outlined-name"
                  label="Company/Airline"
                  name="company"
                  value={values.company}
                  variant="outlined"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.company && !!touched.company}
                  helperText={!!errors.company && !!touched.company && errors.company}
                  margin="normal"
                  className={input}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="outlined-name"
                  label="Number of passengers"
                  name="numberOfPassengers"
                  value={values.numberOfPassengers}
                  onChange={handleChange}
                  variant="outlined"
                  onBlur={handleBlur}
                  error={!!errors.numberOfPassengers && !!touched.numberOfPassengers}
                  helperText={!!errors.numberOfPassengers && !!touched.numberOfPassengers && errors.numberOfPassengers}
                  margin="normal"
                  className={input}
                  type="number"
                  onWheel={(e) => {
                    // Prevent the input value change
                    (e.target as HTMLInputElement).blur();
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <KeyboardDatePicker
                  format="dd/MM/yyyy"
                  onFocus={() => {}}
                  margin="normal"
                  id="date-picker-inline"
                  label="Departure date"
                  KeyboardButtonProps={{
                    'aria-label': 'change date'
                  }}
                  name="departureDate"
                  inputVariant="outlined"
                  value={values.departure.date}
                  onChange={(date) => setFieldValue('departure.date', date)}
                  onBlur={handleBlur}
                  error={!!errors.departure?.date && !!touched.departure?.date}
                  helperText={!!errors.departure?.date && !!touched.departure?.date && errors.departure?.date}
                  className={input}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="outlined-name"
                  label="Flight number"
                  name="departureFlightNumber"
                  value={values.departure.reference}
                  variant="outlined"
                  onChange={(e) => setFieldValue('departure.reference', e.target.value)}
                  onBlur={handleBlur}
                  error={!!errors.departure?.reference && !!touched.departure?.reference}
                  helperText={!!errors.departure?.reference && !!touched.departure?.reference && errors.departure?.reference}
                  margin="normal"
                  className={input}
                  type="text"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <KeyboardDatePicker
                  format="dd/MM/yyyy"
                  onFocus={() => {}}
                  margin="normal"
                  disabled={!isReturnEnabled}
                  id="date-picker-inline"
                  label="Return date"
                  KeyboardButtonProps={{
                    'aria-label': 'change date'
                  }}
                  name="returnDate"
                  inputVariant="outlined"
                  value={values.return?.date}
                  onChange={(date) => setFieldValue('return.date', date)}
                  onBlur={handleBlur}
                  className={input}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="outlined-name"
                  label="Flight number"
                  name="returnFightNumber"
                  disabled={!isReturnEnabled}
                  value={values.return?.reference}
                  variant="outlined"
                  onChange={(e) => setFieldValue('return.reference', e.target.value)}
                  onBlur={handleBlur}
                  margin="normal"
                  className={input}
                  type="text"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      value={isReturnEnabled}
                      checked={isReturnEnabled}
                      onChange={() => {
                        setIsReturnEnabled((value) => {
                          const newValue = !value;
                          if (!newValue) {
                            setFieldValue('return', null);
                          }

                          return newValue;
                        });
                      }}
                    />
                  }
                  label="Roundtrip"
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <AutoSuggest
                  label="Partner agency"
                  error={!!errors.partner && !!touched.partner}
                  placeholder="Search for partner"
                  value={values.partner || null}
                  name="partner"
                  onChange={(value) => setFieldValue('partner', value)}
                  suggestions={partnerOptions}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  id="outlined-name"
                  label="Net total"
                  name="total"
                  value={values.total}
                  onChange={handleChange}
                  variant="outlined"
                  onBlur={handleBlur}
                  error={!!errors.total && !!touched.total}
                  helperText={!!errors.total && !!touched.total && errors.total}
                  margin="normal"
                  className={input}
                  type="number"
                  onWheel={(e) => {
                    // Prevent the input value change
                    (e.target as HTMLInputElement).blur();
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="outlined-multiline-static"
                  variant="outlined"
                  label="Notes"
                  name="notes"
                  value={values.notes}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.notes && !!touched.notes}
                  helperText={!!errors.notes && !!touched.notes && errors.notes}
                  style={{ width: '100%' }}
                  multiline
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" className={button} type="submit" color="primary">
                  {submitLabel}
                </Button>
              </Grid>
            </Grid>
          </MuiPickersUtilsProvider>
        </Form>
      )}
    </Formik>
  );
};

export default TicketForm;
